import dayjs from "dayjs";
import { firstFilled, parseFormulaDate } from "./procurementHelpers";
import { getProcurementParentReference } from "./procurementHelpers";

const ADVANCED_STATUS_KEYS = ["draft", "pending", "overdue", "completed"];
export const ENTRY_ORIGIN_OPTIONS = [
  { label: "Procore", value: "Procore" },
  { label: "Koncurent", value: "Koncurent" },
  { label: "Deleted - Procore", value: "Ex-Procore" },
];
const WARNING_STATUS_FIELDS = [
  "contractIssuedRelease",
  "submittalIssued",
  "submittalReturned",
  "projectedDeliveryDate",
  "actualDeliveryDate",
  "subCommittedDeliveryDate",
];

// ---------------------------------------------------------------------------
// Column-type resolution
// ---------------------------------------------------------------------------

export function getColumnFilterType(col) {
  if (!col) return "text";
  if (col.type === "int" || col.type === "float") return "number";
  if (col.type === "date") return "date";
  return "text";
}

// ---------------------------------------------------------------------------
// Per-filter matching helpers
// ---------------------------------------------------------------------------

function normalizeFilterText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function parseDateValue(value) {
  if (!value) return null;
  const parsed = parseFormulaDate(value);
  return parsed && parsed.isValid() ? parsed.startOf("day") : null;
}

function matchesTextFilter(rawValue, filter) {
  if (!filter || !filter.type) return true;
  const { type, value } = filter;
  if (!value && type !== "blank" && type !== "notBlank") return true;
  const cell = normalizeFilterText(rawValue);
  const term = normalizeFilterText(value);
  switch (type) {
    case "contains":
      return cell.includes(term);
    case "notContains":
      return !cell.includes(term);
    case "equals":
      return cell === term;
    case "notEqual":
      return cell !== term;
    case "startsWith":
      return cell.startsWith(term);
    case "endsWith":
      return cell.endsWith(term);
    case "blank":
      return cell === "";
    case "notBlank":
      return cell !== "";
    default:
      return cell.includes(term);
  }
}

function matchesNumberFilter(rawValue, filter) {
  if (!filter || !filter.type) return true;
  const { type, value, valueTo } = filter;
  if (value === null || value === undefined) {
    if (type !== "blank" && type !== "notBlank") return true;
  }
  const cell =
    rawValue === "" || rawValue === null || rawValue === undefined
      ? null
      : Number(rawValue);
  const isBlank = cell === null || !Number.isFinite(cell);
  switch (type) {
    case "equals":
      return !isBlank && cell === Number(value);
    case "notEqual":
      return isBlank || cell !== Number(value);
    case "lessThan":
      return !isBlank && cell < Number(value);
    case "lessThanOrEqual":
      return !isBlank && cell <= Number(value);
    case "greaterThan":
      return !isBlank && cell > Number(value);
    case "greaterThanOrEqual":
      return !isBlank && cell >= Number(value);
    case "inRange":
      return !isBlank && cell >= Number(value) && cell <= Number(valueTo);
    case "blank":
      return isBlank;
    case "notBlank":
      return !isBlank;
    default:
      return !isBlank && cell === Number(value);
  }
}

function matchesDateFilter(rawValue, filter) {
  if (!filter || !filter.type) return true;
  const { type, value, valueTo } = filter;
  if (!value && type !== "blank" && type !== "notBlank") return true;

  const cell = parseDateValue(rawValue);
  const isBlank = !cell;

  // Normalize both sides to midnight UTC timestamps so comparisons are apples-to-apples
  const cellTs = cell ? cell.startOf("day").valueOf() : null;
  const filterTs = value
    ? dayjs(value, "YYYY-MM-DD").startOf("day").valueOf()
    : null;
  const filterToTs = valueTo
    ? dayjs(valueTo, "YYYY-MM-DD").startOf("day").valueOf()
    : null;

  switch (type) {
    case "equals":
      return !isBlank && filterTs !== null && cellTs === filterTs;
    case "notEqual":
      return isBlank || filterTs === null || cellTs !== filterTs;
    case "lessThan":
      return !isBlank && filterTs !== null && cellTs < filterTs;
    case "greaterThan":
      return !isBlank && filterTs !== null && cellTs > filterTs;
    case "inRange":
      return (
        !isBlank &&
        filterTs !== null &&
        filterToTs !== null &&
        cellTs >= filterTs &&
        cellTs <= filterToTs
      );
    case "blank":
      return isBlank;
    case "notBlank":
      return !isBlank;
    default:
      return !isBlank && filterTs !== null && cellTs === filterTs;
  }
}

function getCellValueForFilter(row, field) {
  if (field === "specTitle") {
    return firstFilled(row?.specTitle, row?.displayTitle, "");
  }
  return row?.[field] ?? "";
}

function hasCellValue(value) {
  return !["", null, undefined].includes(value);
}

function getDaysUntilDate(value) {
  if (!value) {
    return null;
  }

  let parsedDate = null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [month, day, year] = value.split("-");
    parsedDate = dayjs(`${year}-${month}-${day}`);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    parsedDate = dayjs(value);
  } else {
    parsedDate = dayjs(value);
  }

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.startOf("day").diff(dayjs().startOf("day"), "day");
}

function getWarningStatusForField(field, row) {
  if (!row || (row.rowType && row.rowType !== "submittal") || hasCellValue(row.actualDeliveryDate)) {
    return "";
  }

  if (field === "actualDeliveryDate") {
    return row.actualDeliveryDateStatus || "";
  }

  if (field === "subCommittedDeliveryDate") {
    return row.subCommittedDeliveryDateStatus || "";
  }

  if (field === "projectedDeliveryDate") {
    return row.projectedDeliveryDateStatus === "late" ? "late" : "";
  }

  if (field === "submittalReturned") {
    if (hasCellValue(row.submittalReturned)) {
      return "";
    }

    const diffInDays = getDaysUntilDate(row.submittalApprovalDue);
    if (diffInDays === null || diffInDays > 7) {
      return "";
    }

    return diffInDays > 0 ? "pending" : "late";
  }

  if (field === "submittalIssued") {
    if (hasCellValue(row.submittalIssued)) {
      return "";
    }

    const diffInDays = getDaysUntilDate(row.submittalRequired);
    if (diffInDays === null || diffInDays > 7) {
      return "";
    }

    return diffInDays > 0 ? "pending" : "late";
  }

  if (field === "contractIssuedRelease") {
    if (hasCellValue(row.contractIssuedRelease)) {
      return "";
    }

    const diffInDays = getDaysUntilDate(row.issueContract);
    if (diffInDays === null || diffInDays > 7) {
      return "";
    }

    return diffInDays > 0 ? "pending" : "late";
  }

  return "";
}

function getAdvancedFilterStatusFlags(row) {
  const statusFlags = {
    draft: false,
    pending: false,
    overdue: false,
    completed: false,
  };

  if (!row) {
    return statusFlags;
  }

  if (hasCellValue(row.actualDeliveryDate)) {
    statusFlags.completed = true;
    return statusFlags;
  }

  const warningStatuses = WARNING_STATUS_FIELDS.map((field) =>
    getWarningStatusForField(field, row),
  );

  if (warningStatuses.includes("late")) {
    statusFlags.overdue = true;
  } else if (warningStatuses.includes("pending")) {
    statusFlags.pending = true;
  }

  if (!hasCellValue(row.submittalIssued)) {
    statusFlags.draft = true;
  }

  return statusFlags;
}

function filterRowsByPredicate(rowsData, predicate) {
  const rows = rowsData || [];
  const childrenByParentId = new Map();
  const parentById = new Map();

  rows.forEach((row) => {
    const ownId = firstFilled(row.entryID, row.tempId, row.id);
    if (ownId) {
      parentById.set(ownId, row);
    }

    const parentRef = getProcurementParentReference(row);
    if (parentRef) {
      if (!childrenByParentId.has(parentRef)) {
        childrenByParentId.set(parentRef, []);
      }
      childrenByParentId.get(parentRef).push(row);
    }
  });

  const includedRows = new Set();
  const queue = rows.filter(predicate);

  while (queue.length) {
    const currentRow = queue.pop();
    if (!currentRow || includedRows.has(currentRow)) continue;
    includedRows.add(currentRow);

    const ownId = firstFilled(
      currentRow.entryID,
      currentRow.tempId,
      currentRow.id,
    );

    (childrenByParentId.get(ownId) || []).forEach((child) => {
      if (!includedRows.has(child)) queue.push(child);
    });

    const parentRef = getProcurementParentReference(currentRow);
    if (parentRef) {
      const parentRow = parentById.get(parentRef);
      if (parentRow && !includedRows.has(parentRow)) queue.push(parentRow);
    }
  }

  return rows.filter((row) => includedRows.has(row));
}

function rowPassesAllFilters(row, columnFilterModel, alignedData) {
  if (!columnFilterModel || Object.keys(columnFilterModel).length === 0)
    return true;
  for (const [field, filter] of Object.entries(columnFilterModel)) {
    if (!filter) continue;
    const rawValue = getCellValueForFilter(row, field);
    const colType = getColumnFilterType(alignedData?.[field]);
    let passes;
    if (colType === "number") passes = matchesNumberFilter(rawValue, filter);
    else if (colType === "date") passes = matchesDateFilter(rawValue, filter);
    else passes = matchesTextFilter(rawValue, filter);
    if (!passes) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Tree-safe column filter
// Mirrors filterRowsBySubmittalSearch — builds parent/child maps using
// the same getProcurementParentReference + entryID/tempId/id key as the rest
// of the codebase, then walks up and down from every matching row.
// ---------------------------------------------------------------------------

export function filterRowsByColumnFilters(
  rowsData,
  columnFilterModel,
  alignedData,
) {
  if (!columnFilterModel || Object.keys(columnFilterModel).length === 0) {
    return rowsData || [];
  }

  return filterRowsByPredicate(rowsData, (row) =>
    rowPassesAllFilters(row, columnFilterModel, alignedData),
  );
}

export function filterRowsByAdvancedFilters(
  rowsData,
  advancedFilters,
  tasksDict,
  nestSubmittals = false,
) {
  const statuses = (advancedFilters?.statuses || []).filter((status) =>
    ADVANCED_STATUS_KEYS.includes(status),
  );
  const assignedTo = (advancedFilters?.assignedTo || []).filter(Boolean);
  const entryOrigins = new Set(
    (advancedFilters?.entryOrigins || []).map(normalizeFilterText).filter(Boolean),
  );

  if (!statuses.length && !assignedTo.length && !entryOrigins.size) {
    return rowsData || [];
  }

  const assignedEntryIds = new Set();

  assignedTo.forEach((email) => {
    (tasksDict?.[email]?.entryIDs || []).forEach((entryID) => {
      assignedEntryIds.add(String(entryID));
    });
  });

  const predicate = (row) => {
    const rowStatusFlags = getAdvancedFilterStatusFlags(row);
    const matchesStatus =
      !statuses.length ||
      statuses.some((status) => rowStatusFlags[status]);
    const matchesAssignedTo =
      !assignedTo.length ||
      assignedEntryIds.has(String(firstFilled(row?.entryID)));
    const matchesEntryOrigin =
      !entryOrigins.size || entryOrigins.has(normalizeFilterText(row?.entryOrigin));

    return matchesStatus && matchesAssignedTo && matchesEntryOrigin;
  };

  if (!nestSubmittals) {
    return (rowsData || []).filter(predicate);
  }

  return filterRowsByPredicate(rowsData, predicate);
}

// ---------------------------------------------------------------------------
// Filter summary label (used for tooltip on active filter icon)
// ---------------------------------------------------------------------------

const TEXT_TYPE_LABELS = {
  contains: "contains",
  notContains: "not contains",
  equals: "=",
  notEqual: "≠",
  startsWith: "starts with",
  endsWith: "ends with",
  blank: "blank",
  notBlank: "not blank",
};
const NUMBER_TYPE_LABELS = {
  equals: "=",
  notEqual: "≠",
  lessThan: "<",
  lessThanOrEqual: "≤",
  greaterThan: ">",
  greaterThanOrEqual: "≥",
  inRange: "between",
  blank: "blank",
  notBlank: "not blank",
};
const DATE_TYPE_LABELS = {
  equals: "=",
  notEqual: "≠",
  lessThan: "before",
  greaterThan: "after",
  inRange: "between",
  blank: "blank",
  notBlank: "not blank",
};

export function getFilterSummaryLabel(filter, filterType) {
  if (!filter) return "";
  const { type, value, valueTo } = filter;
  const labelMap =
    filterType === "number"
      ? NUMBER_TYPE_LABELS
      : filterType === "date"
        ? DATE_TYPE_LABELS
        : TEXT_TYPE_LABELS;
  const typeLabel = labelMap[type] || type;
  if (type === "blank" || type === "notBlank") return typeLabel;

  const fmtDate = (v) => {
    if (!v) return "";
    const d = dayjs(v);
    return d.isValid() ? d.format("MM/DD/YYYY") : v;
  };

  const displayValue = filterType === "date" ? fmtDate(value) : value;
  const displayValueTo = filterType === "date" ? fmtDate(valueTo) : valueTo;

  if (type === "inRange")
    return `${typeLabel} ${displayValue} – ${displayValueTo ?? ""}`;
  return `${typeLabel} "${displayValue}"`;
}
